import {
  ATTENTION_GUIDANCE,
  ATTENTION_LABEL,
  ATTENTION_REASONS,
} from '../students/student-attention'
import {
  LIFECYCLE_DESCRIPTION,
  LIFECYCLE_LABEL,
  LIFECYCLE_STAGES,
} from '../students/student-lifecycle'

export interface OperatorFaqDiagram {
  caption: string
  definition: string
}

export interface OperatorFaqScenario {
  label: string
  description: string
}

export const OPERATOR_FAQ_SECTIONS = [
  {
    id: 'students-and-enrollments',
    label: 'Students and enrollments',
  },
  {
    id: 'content-and-history',
    label: 'Content and history',
  },
] as const

export type OperatorFaqSectionId =
  (typeof OPERATOR_FAQ_SECTIONS)[number]['id']

export interface OperatorFaqTopicDefinition {
  id: string
  sectionId: OperatorFaqSectionId
  question: string
  answer: string
  scenarios: readonly (string | OperatorFaqScenario)[]
  nextAction: string
  entrypoints: readonly string[]
  guideAnchor: string
  diagram?: OperatorFaqDiagram
}

/**
 * Code-owned operator instructions. Stable IDs are product contracts used by
 * contextual triggers; never derive them from display text or API values.
 */
export const OPERATOR_FAQ_TOPICS = [
  {
    id: 'student-lifecycle',
    sectionId: 'students-and-enrollments',
    question: 'What does the student lifecycle mean?',
    answer:
      'Lifecycle is a neutral summary of where the student is in the focused enrollment. It does not itself mean that an operator must act. Attention badges are separate and identify situations that may need intervention.',
    scenarios: LIFECYCLE_STAGES.map((stage) => ({
      label: LIFECYCLE_LABEL[stage],
      description: LIFECYCLE_DESCRIPTION[stage],
    })),
    nextAction:
      'Use lifecycle to understand the current phase. Act only when an attention badge, account-access message, or enrollment status gives you a concrete next step.',
    entrypoints: ['footer', 'student-detail', 'student-roster'],
    guideAnchor: 'student-lifecycle',
  },
  {
    id: 'student-attention',
    sectionId: 'students-and-enrollments',
    question: 'What do the attention reasons mean?',
    answer:
      'Attention is a worklist signal, separate from lifecycle. A student can have more than one reason at once because invitation delivery, onboarding, learning quality, access, and progress transfer are checked independently.',
    scenarios: ATTENTION_REASONS.map((reason) => ({
      label: ATTENTION_LABEL[reason],
      description: ATTENTION_GUIDANCE[reason],
    })),
    nextAction:
      'Read every current reason, address the concrete next steps that are available, and leave unrelated lifecycle or enrollment history unchanged.',
    entrypoints: ['footer', 'student-detail', 'student-roster'],
    guideAnchor: 'student-attention',
  },
  {
    id: 'cohort-enrollment-defaults',
    sectionId: 'students-and-enrollments',
    question: 'What changes when I edit cohort dates or invite timing?',
    answer:
      'A cohort supplies defaults when an enrollment is created. The enrollment keeps its own access dates, and its invitation delivery keeps the send time resolved at that moment. Editing the cohort affects only enrollments created afterwards; it never silently moves an existing student’s period or queued invitation.',
    scenarios: [
      {
        label: 'Relative invite timing',
        description:
          'For an October 1 start and a seven-day lead, a new enrollment stores October 1 as its start and September 24 as its invitation send time.',
      },
      {
        label: 'Fixed invite timing',
        description:
          'The chosen date and time is copied into each new enrollment’s invitation delivery. It does not follow later cohort edits.',
      },
      {
        label: 'No schedule',
        description:
          'Each new enrollment queues its invitation immediately. Existing deliveries still keep their current state and timing.',
      },
      {
        label: 'Later cohort edit',
        description:
          'New enrollments use the revised dates and invite timing. Existing enrollment dates and invitation deliveries remain unchanged.',
      },
    ],
    nextAction:
      'Before adding students, verify the cohort defaults and resolved invite date. If one existing student needs another activation email, use the action on that student; changing the cohort is not a reschedule operation.',
    entrypoints: ['footer'],
    guideAnchor: 'cohort-defaults-and-invite-timing',
    diagram: {
      caption:
        'Each enrollment resolves the cohort defaults once; later edits start a new line for later enrollments.',
      definition: `sequenceDiagram
        participant O as Operator
        participant C as Cohort defaults
        participant A as Enrollment A
        participant B as Enrollment B
        O->>C: Set Oct 1 start and invite 7 days before
        C->>A: Lock Oct 1 and queue Sep 24
        O->>C: Change to Nov 1 and invite 3 days before
        Note over A: Dates and Sep 24 delivery stay unchanged
        C->>B: Lock Nov 1 and queue Oct 29`,
    },
  },
  {
    id: 'continuity-choices',
    sectionId: 'students-and-enrollments',
    question: 'What should start fresh, and what can be preserved?',
    answer:
      'Each new private enrollment has its own dates, plan, trophy goal, and HR visibility. Placement may be reused on its own. Compatible curriculum or trophy progress can continue only from one earlier enrollment selected as the source. The earlier history never moves. A learner’s chosen practice level belongs to that enrollment. A new period does not inherit the earlier choice.',
    scenarios: [
      'Start fresh when the student should repeat placement or begin a clean course attempt.',
      'Reuse placement when an eligible earlier result still represents the student’s level.',
      'Preserve curriculum or trophies when the new course is compatible and the operator intends a true continuation.',
    ],
    nextAction:
      'Review the values shown beside each option, then confirm the source enrollment and the new enrollment’s own trophy goal.',
    entrypoints: ['footer', 'continuity-form'],
    guideAnchor: 'choosing-what-to-preserve',
  },
  {
    id: 'practice-level',
    sectionId: 'students-and-enrollments',
    question: 'How does practice level differ from evaluated level?',
    answer:
      'Evaluated level is the assessment result from this enrollment’s diagnostic or reused placement evidence. Practice level is the level used for Home recommendations and summary topic counts: the learner’s saved choice, or the initial level when no choice is saved. A choice changes practice without changing the assessment result.',
    scenarios: [
      'Matching levels: evaluated B1 and practice B1 mean practice is at the assessed level.',
      'Higher override: evaluated B1 and practice B2, labelled Learner’s choice, means the student selected more demanding practice. This can help explain difficulty, alongside their recent lesson results.',
      'Initial level means no separate choice is saved. It can still differ from the assessment if the student initially accepted a different level.',
      'Students can choose a level during onboarding and change their saved practice level in Account.',
    ],
    nextAction:
      'Compare both levels and review recent lesson results with the student. If practice feels too difficult, guide them to choose a lower level in Account. Admin shows these values read-only; a new enrollment does not inherit the earlier choice.',
    entrypoints: ['footer', 'practice-level'],
    guideAnchor: 'practice-and-evaluated-levels',
  },
  {
    id: 'continuity-waiting',
    sectionId: 'students-and-enrollments',
    question: 'Why is a progress transfer waiting?',
    answer:
      'The system reserves the earlier enrollment so no other replacement can consume it. It transfers progress automatically after that enrollment has ended and any active lesson or delayed learning update has settled. Once the new enrollment’s level has been confirmed, retiring a lesson or theme does not block the transfer. A normal wait needs no operator action.',
    scenarios: [
      'Before the new start date, waiting is expected and stays quiet.',
      'If the earlier enrollment is suspended, its reservation stays in place but it is not treated as ended.',
      'Progress from a retired lesson or theme stays visible in the renewed enrollment, but the student cannot choose that content for a new lesson.',
      'On or after the new start date, a blocked transfer appears as attention so an operator can inspect the earlier enrollment.',
    ],
    nextAction:
      'If attention is shown, check whether the earlier enrollment should be ended and whether a lesson or background update is still pending. Retry is a repair fallback; automatic recovery continues in the background.',
    entrypoints: ['footer', 'continuity-status', 'student-attention'],
    guideAnchor: 'when-a-transfer-waits',
    diagram: {
      caption:
        'The destination waits without taking progress until the source is safely settled.',
      definition: `sequenceDiagram
        participant O as Operator
        participant S as Earlier enrollment
        participant A as Automatic finalizer
        participant D as New enrollment
        O->>D: Create enrollment and preserve progress
        D->>S: Reserve eligible source
        S-->>A: End and settle learning updates
        A->>D: Transfer eligible starting progress
        D-->>O: Ready automatically`,
    },
  },
  {
    id: 'enrollment-lifecycle-actions',
    sectionId: 'students-and-enrollments',
    question: 'When should I suspend, end, or end and replace?',
    answer:
      'Suspend is a temporary access hold and keeps the same enrollment. End today is a permanent early stop without an immediate successor. End and replace permanently closes the current period and creates a separate non-overlapping enrollment in one operation.',
    scenarios: [
      'Suspend when the same paid period should resume later; its end date does not move.',
      'End today for a withdrawal or other permanent stop.',
      'End and replace for a new plan, course, ownership arrangement, or paid extension.',
    ],
    nextAction:
      'Choose the action that matches the commercial decision and record a concise reason another operator can understand.',
    entrypoints: ['footer', 'enrollment-actions'],
    guideAnchor: 'lifecycle-actions',
    diagram: {
      caption:
        'End and replace saves both sides together, so operators never create an overlapping gap state.',
      definition: `sequenceDiagram
        participant O as Operator
        participant C as Current enrollment
        participant N as Replacement
        O->>C: Review end and replace
        O->>N: Review new terms and continuity
        O->>C: Confirm once
        C->>C: End after today in Japan
        C->>N: Create non-overlapping replacement
        N-->>O: Both changes saved together`,
    },
  },
  {
    id: 'student-access-states',
    sectionId: 'students-and-enrollments',
    question: 'What does the student see in each enrollment state?',
    answer:
      'Sign-in belongs to the student identity. Enrollment state controls study actions separately, while account, history, trophies, and certificates remain reviewable wherever the approved retention policy allows.',
    scenarios: [
      {
        label: 'Scheduled',
        description:
          'Home shows the exact start date. Onboarding, the lesson picker, and new lesson starts stay closed; returning students can review earlier history.',
      },
      {
        label: 'Active',
        description:
          'Normal onboarding and study behavior is available once the enrollment prerequisites are ready.',
      },
      {
        label: 'Paused',
        description:
          'Home says “Study access is paused.” Review stays available and new starts close immediately. The student never sees the operator reason.',
      },
      {
        label: 'Ended',
        description:
          'Home shows the effective end date and review deadline. New study closes; historical review remains available for the approved window.',
      },
      {
        label: 'Between enrollments',
        description:
          'Home neutrally says no program is active. Review stays available. A valid future enrollment takes precedence and shows Scheduled instead.',
      },
      {
        label: 'Preparing',
        description:
          'A due enrollment that is not ready shows a nontechnical preparation message. It exposes no continuity reason or promised recovery time.',
      },
      {
        label: 'Cancelled or replaced',
        description:
          'A cancelled unused period never grants access or looks complete. For a replacement, the successor controls current or scheduled access and the source remains history.',
      },
    ],
    nextAction:
      'Use the enrollment action that matches the commercial decision, then verify Home and the review/start capabilities against this matrix. Do not use retired participation controls to repair a mismatch.',
    entrypoints: ['footer', 'enrollment-actions'],
    guideAnchor: 'student-access-states',
  },
  {
    id: 'continuity-history',
    sectionId: 'students-and-enrollments',
    question: 'What remains visible after renewal or replacement?',
    answer:
      'Every enrollment remains a separate historical period. Its dates, plan, learning program, trophy goal, trial classification, progress, and HR visibility stay attached to that period. Preserving progress gives the new enrollment an eligible starting point; it does not rewrite the old record.',
    scenarios: [
      'Students can review their own past enrollment history after access ends.',
      'Progress for a retired lesson or theme stays visible. If the lesson or theme is made available again, the student keeps that earlier progress.',
      'HR sees only company-owned enrollment periods within their scope, not a student’s private periods.',
      'A cancelled unused enrollment remains visible for audit but has no learning progress.',
    ],
    nextAction:
      'Open the relevant historical enrollment when checking terms, progress evidence, or who should be able to see it.',
    entrypoints: ['footer', 'continuity-status'],
    guideAnchor: 'history-and-visibility',
  },
  {
    id: 'program-default-tutor',
    sectionId: 'content-and-history',
    question: "How does a Program's default tutor affect tutor selection?",
    answer:
      "The Program default is the fallback tutor for regular lessons. It does not choose the diagnostic tutor: every onboarding diagnostic uses Anna, whatever the Program default is. A learner's eligible tutor choice takes priority for a regular lesson, while the Program default keeps the lesson available when no usable choice exists.",
    scenarios: [
      {
        label: 'Diagnostic call',
        description:
          'Diagnostics always use Anna. The product sets this on the server, so neither the Program default nor a learner tutor choice can change it.',
      },
      {
        label: 'Regular lesson with an eligible choice',
        description:
          "The learner's chosen tutor wins when that tutor is active and supports the learner's effective level. The Program default does not override that choice.",
      },
      {
        label: 'No usable saved choice',
        description:
          "Home presents the Program default when the learner has no saved tutor or that tutor is missing, retired, or outside the learner's level range. A lesson start without a tutor choice also resolves to the Program default.",
      },
      {
        label: 'Invalid explicit choice',
        description:
          'A direct call request that names a tutor outside the learner’s effective level is rejected rather than silently switched. The normal Home flow avoids presenting that choice and uses the Program default instead.',
      },
      {
        label: 'Program readiness',
        description:
          'An active Program needs an active default tutor with no level restriction and complete mission and coach-behavior prompts. This ensures its regular-lesson fallback can start at every level. Separately, a deploy fails unless Anna is active with a usable published revision, because every diagnostic depends on her.',
      },
    ],
    nextAction:
      "When a diagnostic cannot start with its coach, inspect Anna's Tutor status and current published revision. When a regular-lesson fallback fails, inspect the Program's Default tutor and that Tutor's status and prompt definitions. Correct the repository YAML and release the content; do not work around the failure by changing learner history.",
    entrypoints: ['footer', 'program-detail'],
    guideAnchor: 'program-default-tutor',
    diagram: {
      caption:
        'Diagnostics always use Anna. Regular lessons prefer an eligible learner choice and otherwise use the Program default.',
      definition: `flowchart LR
        S[New call] --> K{Diagnostic or regular?}
        K -->|Diagnostic| A[Anna]
        K -->|Regular| E{Usable learner choice?}
        E -->|Yes| L[Learner chosen tutor]
        E -->|No| D[Program default tutor]
        E -. Explicit invalid choice .-> R[Reject request]
        A --> C[Start with resolved tutor]
        D --> C
        L --> C`,
    },
  },
  {
    id: 'diagnostic-scenarios',
    sectionId: 'content-and-history',
    question: 'How does a diagnostic differ from a regular scenario?',
    answer:
      'Both are Scenario records and follow the same publication and retirement rules. A regular scenario belongs to a Theme within one Program level and provides a lesson after placement. A diagnostic is referenced directly by a Program because it runs before the learner has a level; it measures placement rather than teaching one level’s curriculum.',
    scenarios: [
      {
        label: 'Place in the catalog',
        description:
          'A regular scenario follows Program → Level → Theme → Scenario. A diagnostic follows Program → Diagnostic scenario, without a Level or Theme.',
      },
      {
        label: 'Shared diagnostics',
        description:
          'More than one Program can reference the same diagnostic Scenario. It remains one content identity and lifecycle: retiring the diagnostic makes it unavailable to every Program, while retiring only one Program blocks it only in that Program context.',
      },
      {
        label: 'Vocabulary and Coverage',
        description:
          'These reports measure content within a selected Program level. They include regular scenarios under that level and exclude diagnostics because a diagnostic has no level.',
      },
      {
        label: 'Calls and history',
        description:
          'Diagnostics are used for onboarding and placement calls. Regular scenarios are used for learning calls. Both retain their own status, availability and started-call history.',
      },
      {
        label: 'Admin Scenarios list',
        description:
          'The list includes both kinds. Use the Kind column and hierarchy links to distinguish them; a shared diagnostic can show links to multiple Programs.',
      },
    ],
    nextAction:
      'Use the diagnostic detail and history when investigating onboarding or placement. Use a Program level’s regular scenarios for Vocabulary and Coverage. Do not attach a diagnostic to a Theme merely to make it appear in level reports.',
    entrypoints: ['footer', 'diagnostic-detail'],
    guideAnchor: 'diagnostic-and-regular-scenarios',
    diagram: {
      caption:
        'Several Programs may reference one shared diagnostic directly, while a regular scenario belongs to one Level and Theme.',
      definition: `flowchart TB
        P1[Program A] --> D[Shared diagnostic scenario]
        P2[Program B] --> D
        P1 --> L[Program level]
        L --> T[Theme]
        T --> R[Regular scenario]
        D --> O[Onboarding and placement call]
        R --> C[Learning call]
        R --> Q[Level Vocabulary and Coverage]
        D -. No level, excluded .-> Q`,
    },
  },
  {
    id: 'content-release-workflow',
    sectionId: 'content-and-history',
    question: 'How does reviewed repository content reach this environment?',
    answer:
      'A forward deployment uses its exact API image to validate the complete YAML catalog, reconcile stable identities, import immutable lesson and Tutor revisions, publish changed revisions, and verify the database state in one transaction. Admin is read-only; it shows the result but cannot publish or correct authored content.',
    scenarios: [
      {
        label: 'Successful release',
        description:
          'The transaction commits with one current published revision. New calls pin that revision when the content and its hierarchy are available.',
      },
      {
        label: 'Catalog transaction fails',
        description:
          'Reconciliation, import, publication, and verification roll back together. The previous revision remains current.',
      },
      {
        label: 'Application fails after catalog publication',
        description:
          'The catalog may already be forward. Rolling back application images deliberately leaves content forward; an explicit content rollback is required to change the current content revision.',
      },
      {
        label: 'Release evidence',
        description:
          'The deployment run retains a metadata-only report for 90 days. It identifies the environment, source commit, API image, run, attempt, catalog scope, and verification result. It does not contain lesson or Tutor wording.',
      },
      {
        label: 'What each record proves',
        description:
          'The reviewed PR is quality evidence. The deployment report proves that one image completed the catalog transaction. Content history records availability and started-call use in this environment.',
      },
    ],
    nextAction:
      'For a normal change, review the YAML diff and use the normal forward deployment. If the run fails, inspect its catalog error and do not repair it through Admin or direct database edits. Use Revisions and Release history to inspect saved versions, and Content history to answer what learners could use.',
    entrypoints: ['footer'],
    guideAnchor: 'content-release-workflow',
    diagram: {
      caption:
        'Repository review, automated release, and environment evidence are separate steps with separate records.',
      definition: `flowchart LR
        A[Author edits typed YAML] --> R[PR review]
        R --> C[CI validates the complete catalog]
        C --> D[Forward deployment uses exact API image]
        D --> T[One catalog transaction]
        T --> P[Current published revisions]
        T --> E[Metadata-only release report]
        P --> N[New calls pin current revisions]
        P --> H[Admin revision and content history]`,
    },
  },
  {
    id: 'content-revisions',
    sectionId: 'content-and-history',
    question: 'How do revisions and release history differ from content history?',
    answer:
      'A revision is one immutable authored version of a lesson, diagnostic, or Tutor. Normal publication leaves exactly one current published revision. Revision numbers record creation order: rollback can make an older number current, and re-publication can make a newer previously published revision current again. Release history records revision actions in this environment; Content history records when learners could use the content.',
    scenarios: [
      {
        label: 'Normal published state',
        description:
          'Exactly one revision is current and published, while older published revisions are retired. New calls pin the current revision when the content and its Program hierarchy are available.',
      },
      {
        label: 'Publish, rollback and re-publication',
        description:
          'Publishing a new revision atomically retires the current revision and makes the new one current. Rollback moves to an older previously published revision. If that rollback is later reversed, re-publication moves forward to the newer previously published revision. Each action retires the revision it replaces.',
      },
      {
        label: 'Existing calls and new calls',
        description:
          'Calls already admitted keep their exact lesson and Tutor revision pins through publication, rollback, re-publication, or retirement. New calls use whichever revision is current after the action, provided the content and its hierarchy are available.',
      },
      {
        label: 'Full retirement and restoration',
        description:
          'Revision-level retirement leaves an active Scenario or Tutor identity with no current published revision, so new calls cannot select it. Restoration makes one previously published revision current again. A retired stable identity is a separate catalog state and cannot be reactivated from a revision page.',
      },
      {
        label: 'Content rollback versus application rollback',
        description:
          'A content rollback changes which saved content revision is current. Rolling back application code is a separate deployment operation and does not by itself rewind content stored in the environment database.',
      },
      {
        label: 'Legacy snapshot or missing evidence',
        description:
          'A migration snapshot can preserve old wording without complete repository or lifecycle provenance. The page labels that limitation instead of guessing who published it or when.',
      },
      {
        label: 'Never available to learners',
        description:
          'This label marks an unpublished revision that has no evidence of ever becoming current in this environment. It is abnormal release or legacy evidence, remains unavailable to learners, and should prompt an engineer to inspect Release history rather than publish it from Admin.',
      },
      {
        label: 'Why Admin offers one action or none',
        description:
          'The exact revision page uses one current-state snapshot. Current content can be retired. A previously published older revision can be a rollback target; a newer one can be re-published; either can be restored when no revision is current. Unpublished, unreadable, or superseded revisions and revisions of a non-active identity remain history only.',
      },
      {
        label: 'Current does not always mean available',
        description:
          'Current identifies the revision selected for future calls. Learners can use it only when the stable identity and its Program hierarchy are also available. Revision actions do not activate a retired Scenario, Tutor, Theme, or Program.',
      },
      {
        label: 'Availability affected by retirement',
        description:
          'Before retirement, Admin names the affected Program or Level. A diagnostic required by an active Program cannot be retired from Admin; fix its repository YAML and publish the corrected revision instead. Anna cannot be retired because every diagnostic call depends on her. A Tutor required as an active Program default is also blocked until another valid default is selected. Retiring the last usable lesson remains an emergency action and makes that Level unavailable for new calls and assignment. An unchanged release then remains blocked until the revision is restored or a corrected new YAML revision is published.',
      },
      {
        label: 'How corrected YAML ends an emergency retirement',
        description:
          'Deploying unchanged YAML keeps the content retired. When a reviewed change creates a genuinely new author revision, the normal atomic release can publish that successor directly. The unsafe revision is never restored in between. Admin still cannot publish drafts, and a manual import cannot use this release-only path.',
      },
    ],
    nextAction:
      'Open Revisions from the lesson, diagnostic, or Tutor detail. Current published identifies what new calls can pin. An exact revision page offers only the operation valid for its current snapshot, or explains why no operation is allowed. Review the stated source and target revisions, enter a reason, and confirm. If Admin says the content changed, refresh and inspect Release history before deciding again; the stale request changed nothing. Compare shows authored field changes, Release history shows lifecycle actions, and Content history shows learner availability over time. A call detail links to the exact revisions it used. If Admin ever shows an unpublished draft after a deployment, treat it as abnormal release evidence and ask an engineer to inspect the run.',
    entrypoints: ['footer', 'content-detail', 'content-revisions'],
    guideAnchor: 'content-revisions-and-release-history',
    diagram: {
      caption:
        'The exact revision and current revision together determine the single available action. Identity and payload guards can leave the page intentionally read-only.',
      definition: `flowchart TD
        A[Open exact revision] --> B{Stable identity active}
        B -->|No| H[History only]
        B -->|Yes| C{Selected revision is current}
        C -->|Yes| R[Retire]
        C -->|No| D{Previously published and readable}
        D -->|No| N[History only]
        D -->|Yes| E{Another current revision exists}
        E -->|No| S[Restore]
        E -->|Older target| O[Rollback]
        E -->|Newer target| P[Re-publish]`,
    },
  },
  {
    id: 'content-history-evidence',
    sectionId: 'content-and-history',
    question: 'Is unknown content history a problem, and what should I do?',
    answer:
      'Content history reports only what this environment recorded. An unknown period means the saved records cannot prove the content’s status, availability, or use for that time. It does not mean the content was active, unavailable, unused, or broken.',
    scenarios: [
      {
        label: 'Before recorded history',
        description:
          'This is expected when the selected dates begin before the “Recorded history starts” date, including in a new environment or just after history tracking was introduced. Start the report at that date. No repair is needed for the earlier period.',
      },
      {
        label: 'Status or parent history is missing',
        description:
          'The system cannot connect all saved status and parent changes into one continuous history. If this affects time after “Recorded history starts”, treat it as a possible anomaly and ask an engineer to inspect it before making a release or retirement decision.',
      },
      {
        label: 'Call-use evidence is incomplete',
        description:
          'A retained call may lack a confirmed voice-session start, an archived call may be outside the query, or older calls may not identify the required Program or tutor prompt. The displayed count may be lower than actual use; no action is needed unless a decision depends on that count.',
      },
      {
        label: 'The window is in the future or too broad',
        description:
          'Future time has not happened, so end the report at today. A retrieval-limit message means the report found too much evidence to answer safely; shorten the date range and try again.',
      },
    ],
    nextAction:
      'Use the action for the matching case above. If the unknown period is expected and irrelevant, no further action is needed. If it falls after “Recorded history starts” or affects a release, retirement, incident, or learner-use decision, do not infer the answer from today’s catalog; ask an engineer to inspect the saved history records.',
    entrypoints: ['footer', 'content-detail', 'content-history'],
    guideAnchor: 'content-history-evidence',
  },
  {
    id: 'content-history-meaning',
    sectionId: 'content-and-history',
    question: 'How do status, availability, and actual use differ?',
    answer:
      'They answer three separate operator questions. Status describes the content itself. Availability says whether a learner could start a new call with it at that time. Actual use says whether saved call records show that a learner’s voice session started with it during the selected window.',
    scenarios: [
      {
        label: 'Availability period',
        description:
          'One continuous span of time during which the content’s status and new-call availability stayed the same. A new period begins when the content or one of its parents changes in a way that affects either value.',
      },
      {
        label: 'Draft Program or Theme',
        description:
          'Programs and Themes can legitimately remain draft while they are configured. They are unavailable to learners until activated. This differs from an unpublished lesson or Tutor revision left behind by an abnormal release or manual workflow.',
      },
      {
        label: 'Active but unavailable',
        description:
          'A lesson can remain active while its Theme or Program is retired. It is unavailable for new calls even though the lesson’s own status has not changed.',
      },
      {
        label: 'Available but unused',
        description:
          'The content could be selected for a new call, but no saved call record shows a voice session starting with it during the selected window.',
      },
      {
        label: 'Used and later retired',
        description:
          'The use count preserves earlier started calls. Retirement prevents future pickup; it does not rewrite historical calls or interrupt a call that already started.',
      },
    ],
    nextAction:
      'Use status when reviewing the content’s publication state, availability when checking what a learner could start, and actual use when checking recorded learner exposure. Do not use any one value as a substitute for the other two.',
    entrypoints: ['footer', 'content-history'],
    guideAnchor: 'content-history-meaning',
    diagram: {
      caption:
        'Status and parent status determine availability. Actual use is recorded only when a learner starts a voice session.',
      definition: `flowchart TB
        S[Content status] --> A{Content and its parents<br/>are active}
        P[Parent status] --> A
        A -->|Yes| V[Available for a new call]
        A -->|No| N[Unavailable for a new call]
        V -->|Learner starts a voice session| U[Actual use recorded]
        U --> H[Historical use remains recorded]
        N -. does not erase earlier use .-> H`,
    },
  },
] as const satisfies readonly OperatorFaqTopicDefinition[]

export type OperatorFaqTopicId = (typeof OPERATOR_FAQ_TOPICS)[number]['id']

export function operatorFaqTopic(
  id: OperatorFaqTopicId,
): (typeof OPERATOR_FAQ_TOPICS)[number] {
  const topic = OPERATOR_FAQ_TOPICS.find((candidate) => candidate.id === id)
  if (topic === undefined) throw new Error(`Unknown operator FAQ topic: ${id}`)
  return topic
}
